import { useEffect, useState } from 'react';
import EmptyDisplay from '../EmptyDisplay/EmptyDisplay';
import './orders.scss';
import SkeletonLoader from '../SkeletonLoaders/DefaultLoader';
import { useNotification } from '../../context/NotificationContext';
import { loadOrders, getReturnedOrders } from '../../api/requests';
import { ArrowRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import 'animate.css';
import { useQuery } from '@tanstack/react-query';
import Pagination from '../Pagination/Pagination';

const Orders = () => {
 const { notify } = useNotification();
 const [activeTab, setActiveTab] = useState<'orders' | 'returned'>(
  'orders',
 );
 const [page, setPage] = useState(1);
 const [returnedPage, setReturnedPage] = useState(1);
 const pageSize = 10;

 const {
  data: ordersData,
  isLoading: isOrdersLoading,
  isError: isOrdersError,
  error: ordersError,
 } = useQuery({
  queryKey: ['orders', page],
  queryFn: async () => {
   const res = await loadOrders(page, pageSize);
   return res;
  },
  staleTime: 5000,
  refetchOnWindowFocus: true,
  retry: 1,
 });

 const {
  data: returnedOrdersData,
  isLoading: isReturnedLoading,
  isError: isReturnedError,
  error: returnedError,
 } = useQuery({
  queryKey: ['returned-orders', returnedPage],
  queryFn: async () => {
   const res = await getReturnedOrders(returnedPage, pageSize);
   return res;
  },
  staleTime: 5000,
  refetchOnWindowFocus: true,
  retry: 1,
  enabled: activeTab === 'returned',
 });

 useEffect(() => {
  if (isOrdersError) {
   notify.error(ordersError.message);
  }
  if (isReturnedError) {
   notify.error(returnedError.message);
  }
  // eslint-disable-next-line
 }, [isOrdersError, isReturnedError]);

 const dateFunc = (rawDate: string): string => {
  const date = new Date(rawDate);
  const opts: Intl.DateTimeFormatOptions = {
   year: 'numeric',
   month: 'long',
   day: 'numeric',
  };
  return date.toLocaleDateString('en-US', opts);
 };

 const colorFunc = (
  text: string,
 ): 'green' | 'yellow' | 'red' | 'default' => {
  const normalized = text.trim().toLowerCase();
  const colorMap: Record<'green' | 'yellow' | 'red', string[]> = {
   green: [
    'created',
    'ready',
    'delivered',
    'picked_up',
    'picked up',
    'dispatched',
    'assigned to a rider',
    'success',
   ],
   yellow: [
    'awaiting vendor confirmation',
    'awaitingconfirmation',
    'pending pick-up',
    'pendingpickup',
    'returned',
   ],
   red: ['cancelled'],
  };

  for (const [color, keywords] of Object.entries(colorMap) as [
   keyof typeof colorMap,
   string[],
  ][]) {
   if (keywords.some((k) => normalized === k)) {
    return color;
   }
  }
  return 'default';
 };

 const isTxSuccess = (status: string) => {
  return status.toLowerCase() === 'success';
 };

 const handlePageChange = (val: number) => {
  if (activeTab === 'orders') {
   setPage((prev) => prev + val);
  } else {
   setReturnedPage((prev) => prev + val);
  }
 };

 const setNaviFilter = (val: any) => {
  // This is a workaround to make the Pagination component work with our state
  // The Pagination component expects a setNaviFilter function that updates a state object
  // We'll just intercept the update and call our setPage/setReturnedPage
  if (typeof val === 'function') {
   const newVal = val({
    currentPage: activeTab === 'orders' ? page : returnedPage,
   });
   if (activeTab === 'orders') {
    setPage(newVal.currentPage);
   } else {
    setReturnedPage(newVal.currentPage);
   }
  }
 };

 const isLoading =
  activeTab === 'orders' ? isOrdersLoading : isReturnedLoading;
 const currentData =
  activeTab === 'orders'
   ? ordersData?.orderHistory
   : returnedOrdersData?.items;
 const pageInfo =
  activeTab === 'orders'
   ? ordersData?.pageInfo
   : {
      page: returnedOrdersData?.page || 1,
      size: returnedOrdersData?.size || 10,
      total: returnedOrdersData?.total || 0,
      pages: returnedOrdersData?.pages || 1,
     };

 return (
  <div className="orders">
   <div className="tabs">
    <button
     className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
     onClick={() => setActiveTab('orders')}
    >
     Orders
    </button>
    <button
     className={`tab ${activeTab === 'returned' ? 'active' : ''}`}
     onClick={() => setActiveTab('returned')}
    >
     Returned Orders
    </button>
   </div>

   {isLoading && <SkeletonLoader count={4}></SkeletonLoader>}

   {!isLoading && (
    <div className="animate__animated animate__fadeIn animate__fast">
     {(!currentData || currentData.length <= 0) && (
      <EmptyDisplay
       text={
        activeTab === 'orders'
         ? 'No new order yet'
         : 'No returned orders yet'
       }
       icon_class={''}
      ></EmptyDisplay>
     )}

     {currentData && currentData.length >= 1 && (
      <>
       <div className="order-table">
        <div className="order-header">
         <div className="eachH">Order ID</div>
         <div className="eachH">Status</div>
         <div className="eachH">Date</div>
         <div className="eachH">Total</div>
         <div className="eachH"></div>
        </div>

        {currentData.map((data: any, idx: number) => (
         <div
          className="order-item"
          key={idx}
         >
          <div className="eachdata">
           <div className="smalltitle">Order Id</div>
           <span className="orderid">{data.order_id}</span>
          </div>

          {isTxSuccess(data.transaction_status) && (
           <div className="eachdata">
            <div className="smalltitle">Status</div>
            <div className={`orderstatus ${colorFunc(data.status)}`}>
             {data.status}
            </div>
           </div>
          )}

          {!isTxSuccess(data.transaction_status) && (
           <div className="eachdata">
            <div className="smalltitle">Status</div>
            <div className={`orderstatus yellow`}>
             Awaiting Payment
            </div>
           </div>
          )}

          <div className="eachdata">
           <div className="smalltitle">Date</div>
           <span className="text">
            {dateFunc(data.order_date || data.createdAt)}
           </span>
          </div>

          <div className="eachdata">
           <div className="smalltitle">Total</div>
           <span className="text">
            ₦{data.amount.toLocaleString()}
           </span>
          </div>

          <div className="eachdata">
           <NavLink
            to={
             activeTab === 'orders'
              ? `/order/${data.order_id}`
              : `/returned-order/${data.id}`
            }
           >
            <div className="view-order">
             <span>View Order</span>
             <ArrowRight
              size={16}
              strokeWidth={2.5}
             ></ArrowRight>
            </div>
           </NavLink>
          </div>
         </div>
        ))}
       </div>

       {pageInfo && (
        <Pagination
         paginationData={{
          page: pageInfo.page,
          size: pageInfo.size,
          total: pageInfo.total,
          pages: pageInfo.pages,
         }}
         handlePageIncrement={handlePageChange}
         setNaviFilter={setNaviFilter}
        />
       )}
      </>
     )}
    </div>
   )}
  </div>
 );
};

export default Orders;
